import { useFormAugmentedContext } from '@framework/form/form/augmented/FormAugmentedContext.tsx';
import { useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { getAccessibleLabelFromOption } from '@framework/form/inputs/select/useSelectedOptionLabel.tsx';
import { IconArrowsSort, IconChevronDown } from '@tabler/icons-react';
import { Button, Space } from 'antd';
import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TbIcon } from '../../../icon/TbIcon.tsx';
import { type IPopoverHandle, Popover } from '../../../popover/Popover.tsx';
import { useSortDirectionOptions } from '../hooks/useSortDirectionOptions.ts';
import {
  buildSort,
  parseSort,
  type SortDirection,
} from '../utils/sortValue.ts';
import { FilterBarSortPopoverContent } from './FilterBarSortPopoverContent.tsx';

// Valore corrente in evidenza nel bottone (es. "Data modifica").
const SortCurrentValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colorText};
`;
const SortLabel = styled.span`
  font-weight: 300;
`;

export interface IFilterBarSortPopoverProps {
  /** Campi ordinabili: `value` = chiave query, `label` = etichetta mostrata. */
  sortingFields: SelectOption[];
}

/**
 * Bottone "Ordinamento" della FilterBar (gemello di [FilterBarAddPopover]):
 * vive nella toolbar accanto a "Filtri di Ricerca" ed è SEMPRE presente
 * (l'ordinamento è sempre valorizzato). Apre un popover con campo + direzione.
 *
 * Vive DENTRO il `FormHandler` esterno della FilterBar, quindi scrive
 * l'ordinamento (`ordinamenti`) sullo stesso form dei filtri e fa partire lo
 * stesso submit (vedi [useApplyFilterValues]). Conseguenza: applicare un filtro
 * NON perde l'ordinamento e viceversa. `ordinamenti` è già escluso dal check
 * "filtrato" (useIsFiltered) → cambiare solo il sort non accende il banner.
 */
export function FilterBarSortPopover({
  sortingFields,
}: Readonly<IFilterBarSortPopoverProps>) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const setValue = useFormSetValue();
  const augmented = useFormAugmentedContext();
  const popoverRef = useRef<IPopoverHandle>(null);
  const directionOptions = useSortDirectionOptions();

  // Ordinamento COMMESSO (quello in query/form): guida il bottone.
  const ordinamenti = useWatch({ name: 'ordinamenti', control }) as
    | string[]
    | undefined;
  const current = parseSort(ordinamenti);

  // Bozza del popover (campo + direzione in modifica). Annulla / chiusura senza
  // Applica NON devono toccare il valore commesso.
  const [draftField, setDraftField] = useState<string | null>(current.field);
  const [draftDirection, setDraftDirection] = useState<SortDirection>(
    current.direction,
  );

  // Riallinea la bozza al valore reale ad ogni apertura (click sul bottone):
  // qualunque cosa sia successa prima (Annulla, "Rimuovi tutti", chiusura con
  // click esterno), riaprendo si riparte sempre dal valore corrente.
  const resetDraftToCurrent = () => {
    setDraftField(current.field);
    setDraftDirection(current.direction);
  };

  const currentFieldOption = sortingFields.find(o => o.value === current.field);
  // Testo per `aria-label`: la `label` può essere un nodo React, quindi
  // riusiamo l'helper accessibile (prova `labelText`, poi `label`/`title`).
  const currentFieldText =
    getAccessibleLabelFromOption(currentFieldOption, 'labelText') ?? '';
  const currentDirection = directionOptions.find(
    o => o.value === current.direction,
  );

  const ariaLabel = current.field
    ? t(
        'filter-bar.sort.aria',
        'Ordinamento: {{field}}, {{direction}}. Premi per modificare.',
        { field: currentFieldText, direction: currentDirection?.label },
      )
    : t('filter-bar.sort.aria-empty', 'Ordinamento. Premi per scegliere.');

  // Applica: scrive `ordinamenti` sul form esterno e fa partire il submit
  // (stesso meccanismo dei chip filtro), poi chiude il popover.
  const handleApply = () => {
    setValue('ordinamenti', buildSort(draftField, draftDirection), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    augmented.submitForm.current?.();
    popoverRef.current?.close();
  };

  const handleCancel = () => {
    resetDraftToCurrent();
    popoverRef.current?.close();
  };

  return (
    <Popover
      ref={popoverRef}
      content={
        <FilterBarSortPopoverContent
          sortingFields={sortingFields}
          field={draftField}
          direction={draftDirection}
          onFieldChange={setDraftField}
          onDirectionChange={setDraftDirection}
          onApply={handleApply}
          onCancel={handleCancel}
        />
      }
      placement="bottomLeft"
      arrow={false}
      autoFocusField
      allowPortalClicks
    >
      <Button
        type="text"
        size="small"
        onClick={resetDraftToCurrent}
        aria-label={ariaLabel}
        icon={<TbIcon icon={IconArrowsSort} />}
      >
        <SortLabel>{t('filter-bar.sort.label', 'Ordinamento:')}</SortLabel>
        {currentFieldOption != null && currentDirection != null && (
          <Space size={4}>
            <SortCurrentValue>{currentFieldOption.label}</SortCurrentValue>
            <TbIcon icon={currentDirection.icon} />
          </Space>
        )}
        <TbIcon icon={IconChevronDown} />
      </Button>
    </Popover>
  );
}
